/**
 * `useApiQuery` — the one way a component reads server data (UI overhaul
 * PR-2, audit S2). A thin, typed wrapper over TanStack's `useQuery` that adds
 * the things every ClickPay read needs and nothing else:
 *
 *   1. scope — every request carries `X-Operator-Scope`, so every cache
 *      entry belongs to a scope: the key is prefixed with it (`scopedKey`) and
 *      the same question under another operator's book is another entry;
 *   2. cancellation — the query's AbortSignal reaches `fetch` through
 *      `withRequestSignal`, so the ~220 typed API wrappers cancel a superseded
 *      request without growing a parameter (PR-0, `requestSignal.ts`);
 *   3. tab-aware polling — `poll` is a cadence that runs only while THIS tab
 *      is the visible one (`useTabVisible`) and on ONE observer per key
 *      app-wide at the fastest cadence asked for (`polling.ts`), so N readers
 *      never mean N timers and a hidden pane never fetches;
 *   4. a prompt refresh when a hidden tab comes back with stale data, so a
 *      pane never shows minutes-old numbers as if they were live — one
 *      request per key however many readers return together. Two paths:
 *      a BROWSER tab that was backgrounded (`document.visibilityState`) is
 *      the effect at the bottom; a workspace pane hidden by `Activity`
 *      (PR-1b) had its effects and observers unmounted, so on show TanStack's
 *      own refetch-on-subscribe does it (stale or invalidated → one fetch);
 *   5. defaults that are never undefined: TanStack merges options with a
 *      spread, so `staleTime: undefined` would silently mean "always stale".
 *
 * Errors are the `ApiError` the API client throws (or a network `TypeError`);
 * render them with `<ErrorState>`.
 */
import { useEffect, useRef } from 'react'
import {
  hashKey, keepPreviousData, useQueries, useQuery, useQueryClient,
  type PlaceholderDataFunction, type QueryKey, type UseQueryOptions, type UseQueryResult,
} from '@tanstack/react-query'
import { isAbortError, withRequestSignal } from './requestSignal'
import { resolveInterval, usePollOwner, type PollSpec } from './polling'
import { scopedKey } from './keys'
import { useOperatorScope } from './scope'
import { defaultRetry, GC_MS, STALE_MS } from './queryClient'
import { useTabVisible } from '../../tabs/useTabVisible'
import type { ApiError } from './apiError'

export type ApiQueryError = ApiError | Error
export type { PollSpec }

export type ApiQueryOptions<T> = {
  /** `false` holds the query idle (no fetch) until it turns true. */
  enabled?: boolean
  /** Override the client default (30 s). `0` = always refetch on mount. */
  staleTime?: number
  /** Override the client default (45 min). */
  gcTime?: number
  /** Refetch cadence while this tab is visible — a number, or a STABLE
   *  function of the latest answer. One timer per key app-wide. */
  poll?: PollSpec<T>
  /** Keep showing the previous answer while a new key loads (paged tables).
   *  A predicate keeps it only when the previous (unscoped) key qualifies —
   *  e.g. the same tab's earlier page, never another tab's rows under new
   *  columns. */
  keepPrevious?: boolean | ((previousKey: QueryKey) => boolean)
  /** Retries for a real failure (default 1). Probes that poll anyway use 0. */
  retry?: number
  /** Give up on a request after this long — a hung call is a failure the
   *  page can show, not a spinner forever. */
  timeoutMs?: number
}

type RetryOption = number | ((failureCount: number, error: unknown) => boolean)

/** The options this wrapper forwards, with the client defaults filled in —
 *  never an undefined value (see the docblock). Exported for its test. */
export function resolveOptions(
  opts: Pick<ApiQueryOptions<unknown>, 'staleTime' | 'gcTime' | 'retry'>,
): { staleTime: number; gcTime: number; retry: RetryOption } {
  return {
    staleTime: opts.staleTime ?? STALE_MS,
    gcTime: opts.gcTime ?? GC_MS,
    retry: opts.retry ?? defaultRetry,
  }
}

/** A signal that aborts when `signal` does OR after `ms`; the timeout aborts
 *  with a `TimeoutError`, which is a failure (not a cancellation). Browsers
 *  without `AbortSignal.any` (Chrome < 116, Safari < 17.4) keep the plain
 *  signal and simply have no timeout. */
export function withTimeout(signal: AbortSignal, ms: number): AbortSignal {
  if (typeof AbortSignal.any !== 'function' || typeof AbortSignal.timeout !== 'function') return signal
  return AbortSignal.any([signal, AbortSignal.timeout(ms)])
}

export function useApiQuery<T>(
  key: QueryKey,
  fetcher: () => Promise<T>,
  opts: ApiQueryOptions<T> = {},
): UseQueryResult<T, ApiQueryError> {
  const scope = useOperatorScope()
  const fullKey = scopedKey(key, scope)
  const hash = hashKey(fullKey)
  const client = useQueryClient()
  const visible = useTabVisible()
  const enabled = opts.enabled ?? true
  const { staleTime, gcTime, retry } = resolveOptions(opts)
  // A hidden pane never retries a failed request either: it re-reads when it
  // comes back (docblock point 4), so a retry timer must not fire for a tab
  // nobody is looking at.
  const visibleRef = useRef(visible)
  useEffect(() => { visibleRef.current = visible }, [visible])
  const retryPolicy = typeof retry === 'number' ? (n: number, e: unknown) => !isAbortError(e) && n < retry : retry
  const retryWhileVisible = (n: number, e: unknown): boolean => visibleRef.current && retryPolicy(n, e)
  const wantsPoll = enabled && visible && opts.poll != null
  const owner = usePollOwner(hash, wantsPoll, opts.poll)
  const timeoutMs = opts.timeoutMs

  // TanStack types placeholderData against NonFunctionGuard<T>, which a
  // generic wrapper cannot name — the one cast in this module.
  const keep = opts.keepPrevious
  const placeholderFn: PlaceholderDataFunction<T, ApiQueryError, T, QueryKey> | undefined =
    keep === true
      ? keepPreviousData
      : typeof keep === 'function'
        ? (prev, prevQuery) => (prevQuery && keep(prevQuery.queryKey.slice(1)) ? prev : undefined)
        : undefined
  const placeholderData = placeholderFn as unknown as
    UseQueryOptions<T, ApiQueryError, T, QueryKey>['placeholderData']

  const query = useQuery<T, ApiQueryError>({
    queryKey: fullKey,
    queryFn: ({ signal }) =>
      withRequestSignal(timeoutMs ? withTimeout(signal, timeoutMs) : signal, fetcher),
    enabled,
    staleTime,
    gcTime,
    retry: retryWhileVisible,
    placeholderData,
    refetchInterval: wantsPoll && owner ? (q) => resolveInterval(hash, q.state.data) : false,
    refetchIntervalInBackground: false,
  })

  // A BROWSER tab that comes back from the background refreshes promptly if
  // its answer went stale meanwhile — the interval alone would wait a whole
  // cadence. (A workspace pane re-shown by `Activity` never takes this path:
  // its effects were unmounted, so `wasVisible` never saw it hidden; TanStack
  // refetches on re-subscribe instead — docblock point 4.) Staleness is read
  // from the cache here (not from render-tracked result fields, which would
  // re-render every reader twice per fetch), and readers that return
  // together share the one request (`cancelRefetch: false`).
  const { refetch } = query
  const wasVisible = useRef(visible)
  useEffect(() => {
    const returned = visible && !wasVisible.current
    wasVisible.current = visible
    if (!returned || !enabled) return
    const state = client.getQueryCache().get(hash)?.state
    if (!state || state.fetchStatus === 'fetching') return
    const stale = state.isInvalidated || state.dataUpdatedAt === 0 || Date.now() - state.dataUpdatedAt >= staleTime
    if (stale) void refetch({ cancelRefetch: false })
  }, [visible, enabled, hash, staleTime, client, refetch])

  return query
}

export type ApiQuerySpec<T> = {
  key: QueryKey
  fetcher: () => Promise<T>
  /** `false` holds this one idle; its result then stays `isPending`. */
  enabled?: boolean
  staleTime?: number
  gcTime?: number
  retry?: number
}

/**
 * N reads from one component whose count can change between renders — a
 * strip of period tiles is one key per window. The same scope prefix,
 * cancellation and filled-in defaults as `useApiQuery`, through TanStack's
 * `useQueries`, so two specs that name the same key share one request (and
 * share it with any `useApiQuery` reader of that key). No polling, and no
 * `keepPrevious`: `useQueries` matches observers by key hash, so a changed
 * key is a NEW observer with no previous answer to keep — the option would
 * promise paged-table semantics it cannot deliver.
 */
export function useApiQueries<T>(specs: ApiQuerySpec<T>[]): UseQueryResult<T, ApiQueryError>[] {
  const scope = useOperatorScope()
  const queries = specs.map((s): UseQueryOptions<T, ApiQueryError, T, QueryKey> => {
    const { staleTime, gcTime, retry } = resolveOptions(s)
    const retryPolicy = typeof retry === 'number' ? (n: number, e: unknown) => !isAbortError(e) && n < retry : retry
    return {
      queryKey: scopedKey(s.key, scope),
      queryFn: ({ signal }) => withRequestSignal(signal, s.fetcher),
      enabled: s.enabled ?? true,
      staleTime,
      gcTime,
      retry: retryPolicy,
    }
  })
  // `useQueries` types its result from a tuple of option types; a mapped
  // array of one type is exactly `UseQueryResult<T>[]` — the one cast here.
  return useQueries({ queries }) as unknown as UseQueryResult<T, ApiQueryError>[]
}
